import { ReleaseNote } from '../_models/release-note';
import {
  ReleaseNoteCategory,
  ReleaseNoteCategoryDisplay,
} from '../_models/release-note-categories';

export const RELEASE_NOTES: readonly ReleaseNote[] = [
  {
    deviceVersion: 'Device software version: BR4.11.1',
    datePublished: new Date('2026-11-03'),
    categories: [ReleaseNoteCategory.Device, ReleaseNoteCategory.BugFixes],
    header: "What's New at a Glance",
    summary: [
      'Device bugfixes.',
      '"Syncing" message displayed on the LCD when sending data to the web app',
    ],
    notes: [
      {
        sectionCategory: `${ReleaseNoteCategoryDisplay[ReleaseNoteCategory.BugFixes]}`,
        sectionBody: [
          {
            title: '',
            description: [
              'Fixed a rare problem that could cause the device to become unresponsive mid-experiment',
              'Improved handling of Wi-Fi lost connection events',
              'Fixed a couple of sparger control issues',
            ],
          },
        ],
      },
    ],
  },
  {
    webAppVersion: 'Web app version: 1.2.0',
    deviceVersion: 'Device software version: BR4.11.0',
    datePublished: new Date('2026-09-17'),
    categories: [ReleaseNoteCategory.NewFeature, ReleaseNoteCategory.Improvements, ReleaseNoteCategory.BugFixes, ReleaseNoteCategory.Device, ReleaseNoteCategory.UX, ReleaseNoteCategory.WebApp, ReleaseNoteCategory.API],
    header: "What's New at a Glance",
    summary: [
      'New fluorescence and sparging user scripting commands.',
      'Updated calibration data page design for an improved user experience.',
      'Two-factor authentication error recovery and UX improvements.',
      'Bug fixes and improvements.',
    ],
    notes: [
      {
        sectionCategory: `${ReleaseNoteCategoryDisplay[ReleaseNoteCategory.Improvements]}`,
        sectionBody: [
          {
            title: 'Error Handling',
            description: [
              'Added a dedicated error page across the application for users who do not yet belong to a laboratory, providing clearer guidance instead of an empty or broken view.',
            ],
          },
          {
            title: 'Filter Modal',
            description: [
              'Filter options previously shown as dropdowns have been replaced with chip selectors for quicker, at-a-glance selection.',
            ],
          },
          {
            title: 'Loading State Handling',
            description: [
              'Improved loading state handling across the application for a more consistent and responsive experience.',
            ],
          },
          {
            title: 'Calibration Data',
            description: [
              'Refreshed the calibration data page design for a cleaner, more consistent user experience.',
              'Improved OD calibration voltage point submission for a smoother workflow.',
            ],
          },
          {
            title: 'Fluorescence',
            description: [
              'Updated integration time minimum and maximum values.',
            ],
          },
          {
            title: 'Two-Factor Authentication',
            description: [
              'Improved error recovery during the 2FA flow, with more consistent feedback and a smoother user experience throughout.',
            ],
          },
        ],
      },
      {
        sectionCategory: `${ReleaseNoteCategoryDisplay[ReleaseNoteCategory.BugFixes]}`,
        sectionBody: [
          {
            title: 'Device Software Warning',
            description: [
              'Fixed an issue where the device software status icon could incorrectly show as up to date when the software was out of date.',
              'Added a new "Unknown" status for cases where the device software version cannot be determined.',
            ],
          },
          {
            title: 'Control Experiment - Sparging Fields',
            description: [
              'Updated the sparging field grid layout to prevent fields from overlapping.',
            ],
          },
        ],
      },
      {
        sectionCategory: `${ReleaseNoteCategoryDisplay[ReleaseNoteCategory.API]}`,
        sectionBody: [
          {
            title: 'API Logging',
            description: [
              'Improved API logging and error handling for more reliable diagnostics and clearer error responses.',
            ],
          },
        ],
      },
      {
        sectionCategory: `${ReleaseNoteCategoryDisplay[ReleaseNoteCategory.Device]}`,
        sectionBody: [
          {
            title: 'New Features',
            description: [
              'Fluorescence user scripting commands.',
              '`get spargers min/max` flowrate user scripting command.',
            ],
          },
          {
            title: 'Improvements',
            description: [
              'If network connection drops, experiment data will be posted on recovery.',
              'Sparging settings updates print to USB.',
              'OD calibration shows current temperature on LCD.',
            ],
          },
          {
            title: 'Bug Fixes',
            description: [
              'Fix issue in turbidostat where first OD would incorrectly read zero.',
              'Fix user being asked to disable O2 when selecting a calibration.',
              'Fix O2 0% calibration disabling temperature control.',
              'Turbidostat "maintain ODs" will constrain targets to at least 0.1.',
              'OD calibration posts readings to web app in real time ("poll for voltage").',
              'OD calibration correctly listens for "discard" and "finish" messages from web app.',
              'Fix creating experiment with spargers disabled.',
              'Fix updating from web app and device.',
              'Fix turbidostat settings file print to say if disabled.',
              'Fix temperature data not sending for a while.',
            ],
          },
        ],
      },
    ],
  },
  {
    webAppVersion: 'Web app version: 1.1.0',
    deviceVersion: 'Device software version: BR4.10.0',
    datePublished: new Date('2026-07-23'),
    categories: [
      ReleaseNoteCategory.NewFeature,
      ReleaseNoteCategory.Improvements,
      ReleaseNoteCategory.BugFixes,
      ReleaseNoteCategory.Security,
      ReleaseNoteCategory.WebApp,
      ReleaseNoteCategory.Device,
      ReleaseNoteCategory.UX,
      ReleaseNoteCategory.API,
    ],
    header: "What's New at a Glance",
    summary: [
      'New morbidostat experiment mode now available on the web app and device.',
      'Two-factor authentication - organisations can now enforce 2FA for all members.',
      'New administration features.',
      'Bug fixes and improvements.',
    ],
    notes: [
      {
        sectionCategory: `${ReleaseNoteCategoryDisplay[ReleaseNoteCategory.NewFeature]}`,
        sectionBody: [
          {
            title: 'Morbidostat Experiment Mode',
            description: [
              'A new "BETA: Morbidostat" experiment mode is now available when creating an experiment.',
              'This mode requires an external pump module - setup includes a confirmation step to ensure the hardware is in place.',
            ],
          },
          {
            title: 'Two-Factor Authentication',
            description: [
              'Organisation admins (Super Users) can enforce 2FA for all members of their organisation. Users with 2FA outstanding are redirected to the enrolment page when they next log in.',
              'Users can now enrol in two-factor authentication (2FA) if required via a guided setup flow: scan a QR code into any TOTP authenticator app (e.g. Google Authenticator, Microsoft Authenticator), confirm with a 6-digit code, and save the ten single-use recovery codes that are generated.',
              'At login, enrolled users are prompted for their TOTP code or a recovery code, with an option to remember the device.',
              'The User Profile page now shows a 2FA management card with enrolment status, remaining recovery code count (highlighted when three or fewer remain), and an option to generate a new set of codes.',
              'OGI Super Administrators can reset a user\'s 2FA from the Update User page, which triggers a confirmation email to the affected user.',
            ],
          },
          {
            title: 'Administration Features',
            description: [
              'New administration tools for OGI Super Administrators, including new system-wide user and device management views.',
              'OGI Super Administrators can now update an organisation\'s licence type directly from the app.',
            ],
          },
        ],
      },
      {
        sectionCategory: `${ReleaseNoteCategoryDisplay[ReleaseNoteCategory.Improvements]}`,
        sectionBody: [
          {
            title: 'Experiment Dashboard: Filter by Module and Deleted Experiments',
            description: [
              'Two new filters have been added to the Experiments tab filter panel on the dashboard.',
              'Users can filter the experiment list by installed hardware modules (Fluorescence, Oxygen, pH, and Sparging) so only experiments that used the selected modules are shown.',
              'A toggle allows deleted experiments to be included in the list, providing visibility into historical runs that are normally hidden from the default view.',
            ],
          },
          {
            title: 'Improved Error Handling',
            description: [
              'The global HTTP error interceptor has been extended to surface more specific error messages rather than generic fallbacks.',
            ],
          },
        ],
      },
      {
        sectionCategory: `${ReleaseNoteCategoryDisplay[ReleaseNoteCategory.UX]}`,
        sectionBody: [
          {
            title: 'Styling Updates',
            description: [
              'The Create Calibration page and Organisation overview page have been updated with refreshed styling for improved consistency.',
            ],
          },
          {
            title: 'Miscellaneous Fixes',
            description: [
              'The navbar no longer breaks layout on small browser windows.',
              '"Save Changes" buttons on the Update User page are now correctly disabled when no edits have been made.',
              'The Update User page hides the lab dropdown and shows a clear message when all available laboratories have already been assigned to the user.',
            ],
          },
        ],
      },
      {
        sectionCategory: `${ReleaseNoteCategoryDisplay[ReleaseNoteCategory.Device]}`,
        sectionBody: [
          {
            title: 'New Features',
            description: [
              'New "morbidostat" mode.',
              '"measure" commands.',
              '"save OD cal" command.',
              '"echo" command.',
            ],
          },
          {
            title: 'Improvements',
            description: [
              'Improved updater GUI.',
              'Temperature data posted every 3 minutes regardless of sample rate.',
              'Added menu item showing version number.',
              'Added logging for occasional ADC read issue.',
              'Added logging for issue with webapp authentication.',
              'Faster command response times.',
              'Faster scripting commands with the new "ogi.py" Python library.'
            ],
          },
          {
            title: 'Bug Fixes',
            description: [
              'Fix unintended file writes in "quick measure" menus.',
              'Fix crash in "quick measure" menus if USB is not connected.',
              'Fix rare EEPROM read failure.',
              'Fix for hang during "Measuring..." screen in experiments.',
            ],
          },
        ],
      },
    ],
  },
  {
    deviceVersion: 'Device software version: BR4.9.3',
    datePublished: new Date('2026-06-10'),
    categories: [ReleaseNoteCategory.Device],
    header: "What's New at a Glance",
    summary: ['WPA2-Enterprise connectivity improvement.'],
    notes: [
      {
        sectionCategory: `${ReleaseNoteCategoryDisplay[ReleaseNoteCategory.Device]}`,
        sectionBody: [
          {
            title: '',
            description: [
              'Support username in `connect wifi` command for WPA2-Enterprise networks.',
            ],
          },
        ],
      },
    ],
  },
  {
    webAppVersion: 'Web app version: 1.0.0',
    deviceVersion: 'Device software version: BR4.9.1',
    datePublished: new Date('2026-04-29'),
    categories: [
      ReleaseNoteCategory.NewFeature,
      ReleaseNoteCategory.Improvements,
      ReleaseNoteCategory.BugFixes,
      ReleaseNoteCategory.UX,
      ReleaseNoteCategory.WebApp,
      ReleaseNoteCategory.Device,
    ],
    header: "What's New at a Glance",
    summary: [
      'Create and save experiment templates to quickly set up new experiments.',
      'Opt in to receive email notifications for active experiment events.',
      'Update your email address from the user management page.',
      'Bug fixes and improvements.',
    ],
    notes: [
      {
        sectionCategory: `${ReleaseNoteCategoryDisplay[ReleaseNoteCategory.NewFeature]}`,
        sectionBody: [
          {
            title: 'Experiment Templates',
            description: [
              'Save experiment settings as templates to quickly re-use them when starting new experiments.',
              'Browse and manage templates from a dedicated templates page.',
              'Select a template when setting up a new experiment from the Set Parameters page.',
            ],
          },
          {
            title: 'Experiment Email Notifications',
            description: [
              'Opt in to receive email notifications for active experiments, such as when errors occur.',
            ],
          },
          {
            title: 'Copy Settings Across Flasks',
            description: [
              'New "Copy A to B, C, D" buttons on the Set Parameters, Control Experiment and Template pages let you instantly apply Flask A\'s settings to all other flasks.',
            ],
          },
        ],
      },
      {
        sectionCategory: `${ReleaseNoteCategoryDisplay[ReleaseNoteCategory.Improvements]}`,
        sectionBody: [
          {
            title: 'User Management',
            description: [
              'You can now update your email address from the user management page.',
              'Updated page styling for a cleaner look.',
            ],
          },
          {
            title: 'Add Device Page',
            description: [
              'Clearer setup instructions added to the Add Device page.',
              'Updated page styling.',
            ],
          },
          {
            title: 'Set Parameters & Control Experiment Pages',
            description: ['Updated page styling for improved consistency.'],
          },
          {
            title: 'Temperature Control',
            description: [
              'Ambient temperature and culture temperature controls are now independent - both accept values from 0°C to 50°C.',
              'The previous minimum of ambient + 6°C is no longer enforced.',
            ],
          },
          {
            title: 'Error Handling',
            description: [
              'Improved handling of 403 responses - users are now correctly redirected when accessing a page they do not have permission to view.',
            ],
          },
        ],
      },
      {
        sectionCategory: `${ReleaseNoteCategoryDisplay[ReleaseNoteCategory.BugFixes]}`,
        sectionBody: [
          {
            title: 'Device Details Page',
            description: [
              'Fixed an issue where navigation options could be missing when the page first loaded.',
              'Page timestamp label updated from "Last updated" to "Page refreshed" for clarity.',
              'Device settings form is now correctly locked when the device is active, preventing accidental changes.',
            ],
          },
          {
            title: 'Voltage Polling',
            description: [
              'Fixed polling button behaviour: the button label now correctly updates to "Polling" when active, and polling can be stopped using the dedicated stop button.',
            ],
          },
          {
            title: 'Organisation Name',
            description: [
              'Added a maximum length limit for organisation names.',
            ],
          },
          {
            title: 'Calibration Tables',
            description: [
              '"Is Final?" column header renamed to "Status" for improved clarity.',
              'Fixed font size on calibration creation pages when no save locations or slots are available.',
            ],
          },
        ],
      },
      {
        sectionCategory: `${ReleaseNoteCategoryDisplay[ReleaseNoteCategory.Device]}`,
        sectionBody: [
          {
            title: 'New Features',
            description: [
              'Stop experiment command for user scripting.',
              'Low-level hardware commands for user scripting.',
              'Removed temperature control restriction of ambient+6°C.',
              'EEPROM status check on startup.',
            ],
          },
        ],
      },
    ],
  },
  {
    deviceVersion: 'Device software version: BR4.8.2',
    datePublished: new Date('2026-04-14'),
    categories: [ReleaseNoteCategory.Device, ReleaseNoteCategory.BugFixes],
    header: "What's New at a Glance",
    summary: ['Device bugfixes.'],
    notes: [
      {
        sectionCategory: `${ReleaseNoteCategoryDisplay[ReleaseNoteCategory.BugFixes]}`,
        sectionBody: [
          {
            title: '',
            description: [
              'Bugfix for sparging in turbidostat mode.',
              '"Wake up" spargers when device turns on.',
            ],
          },
        ],
      },
    ],
  },
  {
    webAppVersion: 'Web app version: 0.4.1',
    datePublished: new Date('2026-03-12'),
    categories: [
      ReleaseNoteCategory.BugFixes,
      ReleaseNoteCategory.Improvements,
    ],
    header: "What's New at a Glance",
    summary: [
      'Optical density calibration voltage polling button bugfix.',
      'Further improved email diagnostic capabilities.',
    ],
    notes: [
      {
        sectionCategory: `${ReleaseNoteCategoryDisplay[ReleaseNoteCategory.BugFixes]}`,
        sectionBody: [
          {
            title: '',
            description: [
              'When user clicks stop voltage polling button the polling button state now correctly resets to allow user to start polling again if they wish.',
            ],
          },
        ],
      },
      {
        sectionCategory: `${ReleaseNoteCategoryDisplay[ReleaseNoteCategory.Improvements]}`,
        sectionBody: [
          {
            title: '',
            description: ['Further improved email diagnostic capabilities.'],
          },
        ],
      },
    ],
  },
  {
    deviceVersion: 'Device software version: BR4.8.1',
    datePublished: new Date('2026-03-11'),
    categories: [ReleaseNoteCategory.Device, ReleaseNoteCategory.NewFeature],
    header: "What's New at a Glance",
    summary: ['Add user scripting command to connect to wifi network.'],
    notes: [
      {
        sectionCategory: `${ReleaseNoteCategoryDisplay[ReleaseNoteCategory.NewFeature]}`,
        sectionBody: [
          {
            title: '',
            description: [
              'Add user scripting command to connect to wifi network.',
            ],
          },
        ],
      },
    ],
  },
  {
    webAppVersion: 'Web app version: 0.4.0',
    deviceVersion: 'Device software version: BR4.8.0',
    datePublished: new Date('2026-03-05'),
    categories: [
      ReleaseNoteCategory.NewFeature,
      ReleaseNoteCategory.Improvements,
      ReleaseNoteCategory.API,
      ReleaseNoteCategory.Device,
      ReleaseNoteCategory.Security,
      ReleaseNoteCategory.WebApp,
      ReleaseNoteCategory.Performance,
      ReleaseNoteCategory.BugFixes,
      ReleaseNoteCategory.UX,
    ],
    header: "What's New at a Glance",
    summary: [
      'Major graphing and usability improvements across the web app.',
      'Manual calibrations can now be created for all calibration types.',
      'Existing calibrations can now be edited.',
      'Calibrations can now be resent to devices.',
    ],
    notes: [
      {
        sectionCategory: `${ReleaseNoteCategoryDisplay[ReleaseNoteCategory.NewFeature]}`,
        sectionBody: [
          {
            title: 'Manual Calibrations',
            description: [
              'Users can now manually create calibrations for all calibration types (OD, Oxygen, pH, and Sparging) by entering values directly via web app.',
            ],
          },
          {
            title: 'Edit Calibrations',
            description: [
              'Existing calibrations can now be edited after being finalised.',
            ],
          },
          {
            title: 'Resend Calibrations',
            description: [
              'Calibrations can now be resent to devices from the Device Calibrations page.',
            ],
          },
          {
            title: 'Oxygen Calibration Email Notifications',
            description: [
              'Users can now toggle on email notifications for oxygen calibration updates.',
            ],
          },
          {
            title: 'Extended Experiment Data Download',
            description: [
              'Super Users can now download extended experiment data.',
            ],
          },
        ],
      },
      {
        sectionCategory: `${ReleaseNoteCategoryDisplay[ReleaseNoteCategory.Improvements]}`,
        sectionBody: [
          {
            title: 'Graphing Improvements',
            description: [
              'Improved graph performance and responsiveness across the web app.',
              'The pH calibration graph now displays voltage (mV) instead of raw sensor readings.',
              'Added the ability to toggle between scatter and line graph types for experiment data.',
              'Pan and zoom functionality has been added to graphs.',
            ],
          },
          {
            title: 'Admin Management Controls',
            description: ['Improved admin management controls.'],
          },
          {
            title: 'Email Diagnostics',
            description: ['Improved email diagnostic capabilities.'],
          },
        ],
      },
      {
        sectionCategory: `${ReleaseNoteCategoryDisplay[ReleaseNoteCategory.BugFixes]}`,
        sectionBody: [
          {
            title: 'Culture Temperature',
            description: [
              'Updated minimum culture temperature to ambient temperature + 6°C to reflect hardware constraints.',
            ],
          },
        ],
      },
      {
        sectionCategory: `${ReleaseNoteCategoryDisplay[ReleaseNoteCategory.UX]}`,
        sectionBody: [
          {
            title: 'Tables',
            description: [
              'Updated action buttons in tables to use icon buttons or outlined button styling for a cleaner, more consistent appearance.',
              'Used status chips in organisation members/labs table for improved clarity.',
              'Added clickable rows for easier navigation to details pages.',
            ],
          },
          {
            title: 'Device Details Tabs',
            description: [
              'Updated the tab bar in the Device Details page to use new underlined styling for improved navigation.',
            ],
          },
          {
            title: 'Modals',
            description: ['Updated modal styling for improved consistency.'],
          },
        ],
      },
      {
        sectionCategory: `${ReleaseNoteCategoryDisplay[ReleaseNoteCategory.Device]}`,
        sectionBody: [
          {
            title: 'New Features',
            description: [
              'List calibrations for all modules via user scripting.',
            ],
          },
          {
            title: 'Bugfixes',
            description: [
              'Fix issue where folders are not created in calibrations.',
              'Fix dropped characters in serial communication.',
              'Allow measurements in OD calibration when temperature control is disabled but probes are attached.',
              'Disallow selecting same fluorophore in fluorescence module.',
              'Fix sparger settings updates via webapp.',
              'Fix chemostat pumps not turning off when flowrate set to 0 in certain cases.',
              'Fix experiment stopping prematurely when run length or sample interval updated.',
            ],
          },
        ],
      },
    ],
  },
  {
    deviceVersion: 'Device software version: BR4.7.3',
    datePublished: new Date('2026-01-28'),
    categories: [ReleaseNoteCategory.Device, ReleaseNoteCategory.BugFixes],
    header: "What's New at a Glance",
    summary: ['Device firmware bugfixes.'],
    notes: [
      {
        sectionCategory: `${ReleaseNoteCategoryDisplay[ReleaseNoteCategory.BugFixes]}`,
        sectionBody: [
          {
            title: '',
            description: [
              'Fix temperature scripting commands.',
              'Fix issue with temperature in OD calibration when starting from web app.',
            ],
          },
        ],
      },
    ],
  },
  {
    deviceVersion: 'Device software version: BR4.7.2',
    datePublished: new Date('2026-01-20'),
    categories: [ReleaseNoteCategory.Device, ReleaseNoteCategory.BugFixes],
    header: "What's New at a Glance",
    summary: ['Device firmware bugfix.'],
    notes: [
      {
        sectionCategory: `${ReleaseNoteCategoryDisplay[ReleaseNoteCategory.BugFixes]}`,
        sectionBody: [
          {
            title: '',
            description: ['Device firmware bugfix.'],
          },
        ],
      },
    ],
  },
  {
    deviceVersion: 'Device software version: BR4.7.1-SLv7',
    datePublished: new Date('2025-12-16'),
    categories: [ReleaseNoteCategory.Device, ReleaseNoteCategory.BugFixes],
    header: "What's New at a Glance",
    summary: ['Add user scripting interface.', 'Device firmware bugfix.'],
    notes: [
      {
        sectionCategory: `${ReleaseNoteCategoryDisplay[ReleaseNoteCategory.Device]}`,
        sectionBody: [
          {
            title: '',
            description: ['Add user scripting interface.'],
          },
        ],
      },
      {
        sectionCategory: `${ReleaseNoteCategoryDisplay[ReleaseNoteCategory.BugFixes]}`,
        sectionBody: [
          {
            title: '',
            description: ['Fix issue where folders are not created on USB.'],
          },
        ],
      },
    ],
  },
  {
    webAppVersion: 'Web app version: 0.3.0',
    deviceVersion: 'Device software version: BR4.6.0-SLv7',
    datePublished: new Date('2025-11-27'),
    categories: [
      ReleaseNoteCategory.NewFeature,
      ReleaseNoteCategory.Improvements,
      ReleaseNoteCategory.BugFixes,
      ReleaseNoteCategory.API,
      ReleaseNoteCategory.WebApp,
      ReleaseNoteCategory.Device,
      ReleaseNoteCategory.UX,
    ],
    header: "What's New at a Glance",
    summary: [
      'New enhanced fluorescence module with updated parameters and data sets.',
      'Intercom integration.',
      'Added form caching to all pages in order to prevent data loss when users session times out.',
      'Experiment device error logs page added to view device-specific errors during experiments.',
    ],
    notes: [
      {
        sectionCategory: `${ReleaseNoteCategoryDisplay[ReleaseNoteCategory.NewFeature]}`,
        sectionBody: [
          {
            title: 'Fluorescence Module',
            description: [
              'Simplified channel configuration: select from preset fluorescence channels instead of manually defining band edges',
              'Spectrum Data: Experiments now capture fine-grained spectral data across the full wavelength range.',
              'Download detailed spectrum data alongside standard channel data for comprehensive analysis.',
              'Optimised fluorescence channel graph data retrieval for improved performance',
            ],
          },
          {
            title: 'Intercom Integration',
            description: [
              'Added Intercom to provide in-app support chat and messaging for users.',
              'Users can view help articles within the Intercom widget.',
            ],
          },
          {
            title: 'Experiment Device Error Logs',
            description: [
              'Device-specific error logs can now be viewed during an experiment.',
            ],
          },
          {
            title: 'Form Caching',
            description: [
              'Added form caching to all forms to prevent data loss when users session times out.',
            ],
          },
        ],
      },
      {
        sectionCategory: `${ReleaseNoteCategoryDisplay[ReleaseNoteCategory.Improvements]}`,
        sectionBody: [
          {
            title: 'Angular Framework Update',
            description: [
              'Updated components to use Signals for improved performance.',
            ],
          },
          {
            title: 'API Improvements',
            description: [
              'Enhanced code maintainability, validation logic, constants, and documentation',
            ],
          },
        ],
      },
      {
        sectionCategory: `${ReleaseNoteCategoryDisplay[ReleaseNoteCategory.UX]}`,
        sectionBody: [
          {
            title: 'Accessibility Improvements',
            description: ['Added aria labels to icon buttons.'],
          },
          {
            title: 'General Improvements',
            description: [
              'Fixed the table header styling, so headers no longer wrap onto another line when the browser is resized.',
              'Updated grid styling to ensure consistent spacing between form fields.',
            ],
          },
        ],
      },
      {
        sectionCategory: `${ReleaseNoteCategoryDisplay[ReleaseNoteCategory.BugFixes]}`,
        sectionBody: [
          {
            title: '',
            description: [
              'Fixed issue where updating experiment organism, media, or strain fields would fail during active experiments.',
              'Fixed sort by functionality on table headers throughout the web app.',
              'Fixed error handling on delete/discard modals.',
              'Fixed allow close flag on modals to prevent accidental closure during processing.',
            ],
          },
        ],
      },
      {
        sectionCategory: `${ReleaseNoteCategoryDisplay[ReleaseNoteCategory.Device]}`,
        sectionBody: [
          {
            title: 'Device firmware updates',
            description: [
              'Device software updated to BR4.6.0-SLv7 with improved fluorescence module support.',
            ],
          },
        ],
      },
      {
        sectionCategory: `${ReleaseNoteCategoryDisplay[ReleaseNoteCategory.Security]}`,
        sectionBody: [
          {
            title: '',
            description: ['Security improvements and dependency updates.'],
          },
        ],
      },
    ],
  },
  {
    webAppVersion: 'Web app version: 0.2.1 Patch release',
    deviceVersion: 'Device software version: BR4.5.1-SLv6',
    datePublished: new Date('2025-10-28'),
    categories: [
      ReleaseNoteCategory.BugFixes,
      ReleaseNoteCategory.Improvements,
      ReleaseNoteCategory.API,
      ReleaseNoteCategory.WebApp,
      ReleaseNoteCategory.Device,
    ],
    header: "What's New at a Glance",
    summary: [
      'Fixed device bug where ODs are printed incorrectly.',
      'Fixed API bug where devices incorrectly showed as participating in multiple experiments.',
      'Fixed API bug where an error log entry was not created when a Stop Experiment message was quarantined.',
      'Updated calibration naming rules to be more flexible and user-friendly.',
    ],
    notes: [
      {
        sectionCategory: `${ReleaseNoteCategoryDisplay[ReleaseNoteCategory.BugFixes]}`,
        sectionBody: [
          {
            title: 'API bug fixes',
            description: [
              'Fixed API bug where devices incorrectly showed as participating in multiple experiments when a draft created via the web app was in draft locked status, and a new experiment was then created directly on the device.',
              'Fixed API bug where an error log entry was not created when a Stop Experiment message was quarantined.',
            ],
          },
        ],
      },
      {
        sectionCategory: `${ReleaseNoteCategoryDisplay[ReleaseNoteCategory.Device]}`,
        sectionBody: [
          {
            title: 'Device bug fixes',
            description: [
              'Fixed bug where ODs (optical densities) are printed incorrectly.',
            ],
          },
        ],
      },
      {
        sectionCategory: `${ReleaseNoteCategoryDisplay[ReleaseNoteCategory.Improvements]}`,
        sectionBody: [
          {
            title: 'Calibration Updates',
            description: [
              'Calibration names now support all standard printable characters (letters, numbers, punctuation, and symbols).',
              'Spaces and dots can now be used at the start or end of names.',
            ],
          },
        ],
      },
    ],
  },
  {
    webAppVersion: 'Web app version: 0.2.0 Minor release',
    deviceVersion: 'Device software version: BR4.5.0-SLv5',
    datePublished: new Date('2025-10-09'),
    categories: [
      ReleaseNoteCategory.NewFeature,
      ReleaseNoteCategory.Improvements,
      ReleaseNoteCategory.BugFixes,
      ReleaseNoteCategory.Security,
      ReleaseNoteCategory.API,
      ReleaseNoteCategory.WebApp,
      ReleaseNoteCategory.UX,
      ReleaseNoteCategory.Device,
    ],
    header: "What's New at a Glance",
    summary: [
      'Added two new experiment modes.',
      'Improved API error visibility and device status enforcement.',
      'Calibration workflows updated with new input fields.',
      'UX and responsiveness improvements across dashboard and organisation views.',
      'Underlying framework updated for enhanced security and responsiveness.',
    ],
    notes: [
      {
        sectionCategory: `${ReleaseNoteCategoryDisplay[ReleaseNoteCategory.NewFeature]}`,
        sectionBody: [
          {
            title: 'New experiment modes',
            description: [
              'Added two new pH modes: pH 2xControl and pH + Chemostat.',
            ],
          },
        ],
      },
      {
        sectionCategory: `${ReleaseNoteCategoryDisplay[ReleaseNoteCategory.Improvements]}`,
        sectionBody: [
          {
            title: 'API Behaviour',
            description: [
              'Improved error handling with clearer visibility of exceptions.',
              'Experiment graph requests now return a consistent response, even when no data is available.',
              'Prevent users being added to deleted laboratories.',
              'Enforce device status of online and inactive at time of calibration creation via the web app.',
            ],
          },
          {
            title: 'Calibration Updates',
            description: [
              'Create and view calibration pages now use dedicated number input fields.',
              'Added default values for sparging volume and pH temperature fields on create calibration page.',
            ],
          },
          {
            title: 'Experiment Updates',
            description: [
              'Allow target ODs to be set whether maintain OD is selected or not once an experiment is active.',
            ],
          },
          {
            title: 'UX & Responsiveness',
            description: [
              'MAC address validation added to prevent invalid addresses being submitted when adding a device.',
              'Organisation details page polish for smoother user experience.',
              'Dashboard view improvements for responsiveness on different screen sizes.',
              'Display success toast when user removes a device from an experiment on select devices page.',
              'Added a character counter to note input fields.',
              'Fixed styling on the graph legends to prevent text overlap.',
              'Display a warning on create experiment if user enters a value that is outside the recommended range for ambient temperature.',
              'If user tries to reset password and there is no token, we now prompt the user to request a new password reset email.',
              'Improve device card styling on select devices page so if there are no calibrations it is clearer to the user.',
              'If a device is out of sync the device status chip on the dashboard is now grey and an offline icon (cloud) is displayed below.',
              'The refresh buttons on the dashboard and user requests page are now animated when clicked to give user feedback.',
            ],
          },
        ],
      },
      {
        sectionCategory: `${ReleaseNoteCategoryDisplay[ReleaseNoteCategory.BugFixes]}`,
        sectionBody: [
          {
            title: '',
            description: [
              'Fixed issue where OD experiment graph advanced settings allowed a minimum y-axis value below the valid range, which caused an error when correcting the value.',
              'Validation fixes to ensure device properties can be updated correctly.',
              'Refresh button on display experiment now maintains flask selection.',
              'Fixed issue where device select list showed duplicate entries on create calibration page.',
              'Fixed calibration name validation on create calibration page to allow longer names.',
              'Maintain flask selection when the browser is resized and the graph view changes on display experiment page.',
              'Stop the page from scrolling once user reaches min/max zoom on experiment graphs.',
            ],
          },
        ],
      },
      {
        sectionCategory: `${ReleaseNoteCategoryDisplay[ReleaseNoteCategory.Security]}`,
        sectionBody: [
          {
            title: 'Dependencies & Framework',
            description: [
              'Underlying framework updated for enhanced security and responsiveness.',
              'Locked package versions for improved stability following recent npm vulnerability reports.',
            ],
          },
        ],
      },
      {
        sectionCategory: `${ReleaseNoteCategoryDisplay[ReleaseNoteCategory.Device]}`,
        sectionBody: [
          {
            title: '',
            description: [
              'Beta experiment modes communicate with webapp.',
              'Improved & fixed printing experiment parameters updates to file.',
              'Fixed issue where pumps can be left on while updating settings.',
            ],
          },
        ],
      },
    ],
  },
  {
    webAppVersion: 'Web app version: 0.1.0 Soft launch update',
    deviceVersion: 'Device software version: BR4.4.2-SLv3',
    datePublished: new Date('2025-09-01'),
    categories: [
      ReleaseNoteCategory.NewFeature,
      ReleaseNoteCategory.Improvements,
      ReleaseNoteCategory.BugFixes,
      ReleaseNoteCategory.Security,
      ReleaseNoteCategory.API,
      ReleaseNoteCategory.WebApp,
      ReleaseNoteCategory.Device,
      ReleaseNoteCategory.UX,
    ],
    header: "What's New at a Glance",
    summary: [
      'Added sparging module support, including calibrations and experiment setup.',
      'New experiment modes: pH 2xControl and pH + Chemostat (available from device, coming soon to web app).',
      'Extra fluorescence parameters (integration time, gain, band edges).',
      'Major graphing and usability improvements across the web app.',
    ],
    notes: [
      {
        sectionCategory: `${ReleaseNoteCategoryDisplay[ReleaseNoteCategory.NewFeature]}`,
        sectionBody: [
          {
            title: 'Sparging Module Support',
            description: [
              'Added full support for sparging module, including calibrations and ability to include in experiments.',
            ],
          },
          {
            title: 'New Experiment Modes',
            description: [
              'Added handling and display support for pH 2xControl and pH + Chemostat.',
              'Users can now create these modes directly from a device (coming soon to web app).',
            ],
          },
          {
            title: 'Fluorescence Parameters',
            description: [
              'New parameters: integration time, gain (high/low), band edges.',
            ],
          },
        ],
      },
      {
        sectionCategory: `${ReleaseNoteCategoryDisplay[ReleaseNoteCategory.Improvements]}`,
        sectionBody: [
          {
            title: 'Graphing Enhancements',
            description: [
              'Dynamic y-axis scaling on oxygen graphs.',
              'Graph view stays consistent when selecting new data points.',
              'Improved axis labels, legends, and flask data naming.',
              'Better calibration graph views for small data ranges.',
            ],
          },
          {
            title: 'General Usability',
            description: [
              'Increased inactivity timeout to 15 minutes.',
              'Replaced some success modals with toast notifications to reduce clicks.',
              'Simplified error log filtering with in-place filter select.',
              'Added release notes to the “About” page.',
              'Replaced sliders with number inputs for experiment parameter settings.',
            ],
          },
          {
            title: 'API Behaviour Improvements',
            description: [
              'Improved handling of unexpected device status changes - inactive devices now appear opaque on experiment view.',
              'Turbidostat experiments now accept target OD values from devices when “maintain OD” is enabled.',
            ],
          },
        ],
      },
      {
        sectionCategory: `${ReleaseNoteCategoryDisplay[ReleaseNoteCategory.BugFixes]}`,
        sectionBody: [
          {
            title: '',
            description: [
              'Fixed pause/resume buttons on display experiment page.',
              'Fixed pause experiment button on control experiment page.',
              'Fixed navigation to experiments from device dashboard.',
              'Fixed calibration data cards not loading when calibration no longer active.',
              'Fixed bug preventing pH calibration graph polling.',
              'Fixed calibration duplication issue when devices first power on and sync.',
              'Navbar layout improved on reduced screen heights.',
            ],
          },
        ],
      },
      {
        sectionCategory: `${ReleaseNoteCategoryDisplay[ReleaseNoteCategory.Security]}`,
        sectionBody: [
          {
            title: '',
            description: ['Security improvements and dependency updates.'],
          },
        ],
      },
    ],
  },
];
